import { api, API_BASE } from "@/lib/apiClient";

export interface PersonalUploadFile {
  id: number;
  filename: string;
  content_type?: string | null;
  size_bytes?: number | null;
  uploaded_by?: string | null;
  uploaded_at?: string | null;
  expires_at?: string | null;
}

export interface PersonalUploadListResponse {
  files: PersonalUploadFile[];
}

export interface PersonalUploadQrResponse {
  url?: string;
  qr_data_uri?: string;
  expires_at?: string | null;
}

export async function listPersonalUploads(): Promise<PersonalUploadListResponse> {
  return api.get<PersonalUploadListResponse>("/me/uploads");
}

export async function uploadPersonalFile(file: File): Promise<PersonalUploadFile> {
  const fd = new FormData();
  fd.append("file", file);
  return api.postForm<PersonalUploadFile>("/me/uploads", fd);
}

export async function deletePersonalUpload(fileId: number): Promise<void> {
  await api.del(`/me/uploads/${fileId}`);
}

export async function deleteAllPersonalUploads(): Promise<{ deleted: number }> {
  const res = await api.del<{ ok: boolean; deleted: number }>("/me/uploads");
  return { deleted: res.deleted ?? 0 };
}

export async function sendPersonalUploadMail(payload: {
  file_ids: number[];
  to: string;
  subject: string;
  body: string;
  html_body?: string;
}): Promise<{ ok: boolean; via?: string }> {
  return api.post("/me/uploads/send-mail", payload);
}

export function getPersonalUploadUrl(fileId: number, download = false): string {
  const base = API_BASE || "";
  const q = download ? "?download=true" : "";
  return `${base}/me/uploads/${fileId}${q}`;
}

function getPersonalUploadZipUrl(imagesOnly = false): string {
  const base = API_BASE || "";
  const q = imagesOnly ? "?images_only=true" : "";
  return `${base}/me/uploads/download-zip${q}`;
}

const ZIP_PICKER_TYPES = [
  {
    description: "ZIP-Archiv",
    accept: { "application/zip": [".zip"] },
  },
];

interface ZipWritable extends WritableStream<Uint8Array> {
  abort: () => Promise<void>;
}

interface ZipFileHandle {
  createWritable: () => Promise<ZipWritable>;
}

type SaveFilePicker = (options?: {
  suggestedName?: string;
  types?: Array<{ description?: string; accept: Record<string, string[]> }>;
}) => Promise<ZipFileHandle>;

function browserWindow(): (Window & { showSaveFilePicker?: SaveFilePicker }) | null {
  if (typeof globalThis === "undefined") return null;
  const candidate = globalThis as typeof globalThis & {
    window?: Window & { showSaveFilePicker?: SaveFilePicker };
  };
  return candidate.window ?? null;
}

function getSaveFilePicker(): SaveFilePicker | null {
  const win = browserWindow();
  if (!win) return null;
  const picker = win.showSaveFilePicker;
  return typeof picker === "function" ? picker.bind(win) : null;
}

function isSavePickerCancel(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isPickerUnavailable(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === "SecurityError" || err.name === "NotAllowedError")
  );
}

let zipDownloadFrame: HTMLIFrameElement | null = null;

function startBrowserZipDownload(imagesOnly: boolean): void {
  const doc = (globalThis as typeof globalThis & { document?: Document }).document;
  if (!doc?.body) {
    throw new Error("Download fehlgeschlagen.");
  }
  const url = getPersonalUploadZipUrl(imagesOnly);
  const sep = url.includes("?") ? "&" : "?";
  if (!zipDownloadFrame || !zipDownloadFrame.isConnected) {
    zipDownloadFrame = doc.createElement("iframe");
    zipDownloadFrame.hidden = true;
    zipDownloadFrame.name = "handy-pc-zip-download";
    zipDownloadFrame.setAttribute("aria-hidden", "true");
    doc.body.appendChild(zipDownloadFrame);
  }
  // Gleicher src würde den Frame nicht neu laden. Der Server ignoriert `_`.
  zipDownloadFrame.src = `${url}${sep}_=${Date.now()}`;
}

async function readDownloadError(res: Response): Promise<string> {
  let detail = "Download fehlgeschlagen.";
  try {
    const data = await res.json();
    if (typeof data?.detail === "string") detail = data.detail;
  } catch {
    /* ignore */
  }
  return detail;
}

async function writeZipToHandle(handle: ZipFileHandle, imagesOnly: boolean): Promise<void> {
  const res = await fetch(getPersonalUploadZipUrl(imagesOnly), {
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(await readDownloadError(res));
  }
  if (!res.body) {
    throw new Error("Download fehlgeschlagen.");
  }
  const writable = await handle.createWritable();
  try {
    await res.body.pipeTo(writable);
  } catch (err) {
    try {
      await writable.abort();
    } catch {
      /* Stream ist bereits geschlossen. */
    }
    throw err;
  }
}

export async function downloadPersonalUploadsZip(imagesOnly = false): Promise<void> {
  const filename = imagesOnly ? "handy-pc-bilder.zip" : "handy-pc-dateien.zip";
  const picker = getSaveFilePicker();
  if (!picker) {
    startBrowserZipDownload(imagesOnly);
    return;
  }

  // Der Speichern-Dialog muss im Klick entstehen, bevor die ZIP übertragen wird.
  let handle: ZipFileHandle;
  try {
    handle = await picker({
      suggestedName: filename,
      types: ZIP_PICKER_TYPES,
    });
  } catch (err) {
    if (isSavePickerCancel(err)) return;
    if (isPickerUnavailable(err)) {
      startBrowserZipDownload(imagesOnly);
      return;
    }
    throw err;
  }

  await writeZipToHandle(handle, imagesOnly);
}

export async function getPersonalUploadMobileQr(): Promise<PersonalUploadQrResponse> {
  return api.get<PersonalUploadQrResponse>("/me/uploads/mobile-qr");
}

export function isPersonalUploadImage(file: PersonalUploadFile): boolean {
  const ct = (file.content_type || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const name = (file.filename || "").toLowerCase();
  return /\.(png|jpe?g|webp|gif|heic|heif)$/.test(name);
}

export function formatFileSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return "–";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
